import React, { useState, useEffect, useRef, useCallback } from "react";
import { MoreHorizontal, User, History, CreditCard } from "lucide-react";
import { adminService } from "../services/adminService";
import AnalysisHistoryModal from "../components/AnalysisHistoryModal";
import UserProfileModal from "../components/UserProfileModal";
import PaymentHistoryModal from "../components/PaymentHistoryModal";
import SearchBar from "../../../components/ui/SearchBar";
import FilterDropdown from "../../../components/ui/FilterDropdown";
import AvatarCell from "../../../components/ui/AvatarCell";
import PlanBadge from "../../../components/ui/PlanBadge";
import DataTable from "../../../components/ui/DataTable";
import { getAvatarColorByIndex } from "../../../utility/avatarUtils";
import { PLAN_TIERS } from "../../../constants/plans";

const PAGE_SIZE = 10;

const mapUser = (u) => ({
  id:                      u.id,
  name:                    u.name                        ?? "",
  email:                   u.email                       ?? "",
  role:                    u.role,
  company:                 u.company_name                ?? "—",
  plan_tier:               u.plan_tier                   ?? "freemium",
  is_email_verified:       u.is_email_verified,
  is_active:               u.is_active,
  createdAt:               u.created_at                  ?? u.createdAt,
  last_active_at:          u.last_login,
  last_login:              u.last_login,
  team_size:               u.team_size                   ?? null,
  totalAnalyses:           u.analysis_count              ?? 0,
  this_month_analyses:     u.this_month_analysis_count   ?? 0,
});

const PLAN_OPTIONS = [
  { label: "All Plans", value: "all" },
  ...PLAN_TIERS.map((t) => ({ label: t, value: t })),
];

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ── Row Actions Dropdown ──────────────────────────────────────────────────────
const ROW_ACTIONS = [
  { label: "View Profile",     icon: User },
  { label: "Analysis History", icon: History },
  { label: "Payment History",  icon: CreditCard },
];

const ActionsMenu = ({ user, onAnalysisHistory, onViewProfile, onPaymentHistory }) => {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, right: 0 });
  const ref    = useRef(null);
  const btnRef = useRef(null);

  // Close on outside mousedown
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Close on scroll / resize while open
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const toggleOpen = () => {
    if (btnRef.current) {
      const r           = btnRef.current.getBoundingClientRect();
      const menuHeight  = ROW_ACTIONS.length * 44 + 8; // approx height
      const spaceBelow  = window.innerHeight - r.bottom;
      const flipUp      = spaceBelow < menuHeight;
      setPos({
        top:   flipUp ? r.top - menuHeight : r.bottom + 4,
        right: window.innerWidth - r.right,
      });
    }
    setOpen((o) => !o);
  };

  const handleAction = (label) => {
    setOpen(false);
    if (label === "Analysis History") onAnalysisHistory?.(user);
    if (label === "View Profile")     onViewProfile?.(user);
    if (label === "Payment History")  onPaymentHistory?.(user);
  };

  return (
    <div ref={ref}>
      <button
        ref={btnRef}
        onClick={toggleOpen}
        className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-brand-muted hover:bg-surface hover:text-brand-text transition-colors"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div
          style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 9999 }}
          className="w-48 bg-white border border-surface-border rounded-xl shadow-lg py-1 overflow-hidden"
        >
          {ROW_ACTIONS.map(({ label, icon: Icon }) => (
            <button
              key={label}
              onClick={() => handleAction(label)}
              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-brand-text hover:bg-surface transition-colors"
            >
              <Icon className="w-4 h-4 text-brand-muted" />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Table columns ─────────────────────────────────────────────────────────────
const buildColumns = (setHistoryUser, setProfileUser, setPaymentUser) => [
  {
    header: "User",
    render: (user, idx) => (
      <AvatarCell
        name={user.name}
        sub={user.email}
        color={getAvatarColorByIndex(idx)}
      />
    ),
  },
  {
    header: "Plan",
    render: (user) => <PlanBadge plan={user.plan_tier} />,
  },
  // {
  //   header: "Organization",
  //   render: (user) => (
  //     <span className="text-sm text-brand-text">
  //       {user.company ?? "—"}
  //     </span>
  //   ),
  // },
  {
    header: "Overall Analysis",
    align: "center",
    render: (user) => (
      <span className="text-sm text-brand-text">
        {user.totalAnalyses ?? "—"}
      </span>
    ),
  },
  {
    header: "This Month",
    align: "center",
    render: (user) => (
      <span className="text-sm text-brand-text">
        {user.this_month_analyses ?? 0}
      </span>
    ),
  },
  {
    header: "Joined",
    render: (user) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">
        {formatDate(user.createdAt)}
      </span>
    ),
  },
  {
    header: "Last Active",
    render: (user) => {
      const val = user.lastActiveAt ?? user.last_active_at;
      return val
        ? <span className="text-sm text-brand-muted whitespace-nowrap">{formatDate(val)}</span>
        : <span className="text-sm text-brand-muted/60 whitespace-nowrap">N/A</span>;
    },
  },
  {
    header: "Actions",
    align: "right",
    render: (user) => (
      <ActionsMenu
        user={user}
        onAnalysisHistory={setHistoryUser}
        onViewProfile={setProfileUser}
        onPaymentHistory={setPaymentUser}
      />
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────────────────────────
const AdminUserManagementPage = () => {
  const [page,       setPage]       = useState(1);
  const [planFilter, setPlanFilter] = useState("all");
  const [inputValue, setInputValue] = useState(""); // local input, not yet committed
  const [search,     setSearch]     = useState(""); // committed — triggers API

  const [users,   setUsers]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const [historyUser, setHistoryUser] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [paymentUser, setPaymentUser] = useState(null);

  const handlePlanChange   = (val) => { setPlanFilter(val); setPage(1); };
  const handleSearchCommit  = (val) => { setSearch(val);    setPage(1); };
  const handleSearchClear   = ()    => { setSearch("");    setPage(1); };
  const handleSearchChange  = (val) => {
    setInputValue(val);
    if (val === "") { setSearch(""); setPage(1); }
  };

  // ── Fetch user list ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminService.getUsers({
      page,
      limit:    PAGE_SIZE,
      planTier: planFilter !== "all" ? planFilter : undefined,
      search:   search.trim() || undefined,
    })
      .then((res) => {
        if (cancelled) return;
        const raw  = res?.data?.users ?? res?.data ?? [];
        const meta = res?.meta?.pagination ?? {};
        setUsers(Array.isArray(raw) ? raw.map(mapUser) : []);
        setTotal(meta.total ?? 0);
        setError(null);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, planFilter, search]);

  // ── Fetch user detail on View Profile ───────────────────────────────────
  const handleViewProfile = useCallback(async (listUser) => {
    setProfileUser({ ...listUser, _loading: true });
    try {
      const res    = await adminService.getUser(listUser.id);
      // unwrap { success, data: {...} } OR plain object
      const detail = res?.data ?? res;
      // Only merge detail if we actually got valid data back (guard against 304 / empty body)
      if (detail?.id || detail?.name) {
        setProfileUser({
          ...listUser,
          ...mapUser(detail),
          team_size:         detail.team_size         ?? listUser.team_size,
          is_email_verified: detail.is_email_verified ?? listUser.is_email_verified,
          is_active:         detail.is_active         ?? listUser.is_active,
          last_login:        detail.last_login        ?? listUser.last_login,
          last_login_ip:     detail.last_login_ip,
          createdAt:         detail.createdAt ?? detail.created_at ?? listUser.createdAt,
          _loading: false,
        });
      } else {
        // No usable detail — keep list data as-is
        setProfileUser({ ...listUser, _loading: false });
      }
    } catch {
      setProfileUser({ ...listUser, _loading: false });
    }
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const columns    = buildColumns(setHistoryUser, handleViewProfile, setPaymentUser);

  return (
    <>
      <div className="flex flex-col gap-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-brand-text">User Management</h1>
          <p className="text-sm text-brand-muted mt-0.5">Manage platform users</p>
        </div>

        {/* Search + Filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <SearchBar
            value={inputValue}
            onChange={handleSearchChange}
            onCommit={handleSearchCommit}
            onClear={handleSearchClear}
            placeholder="Search name, company or email…"
            className="max-w-sm"
          />
          <div className="flex-1" />
          <FilterDropdown
            value={planFilter}
            onChange={handlePlanChange}
            options={PLAN_OPTIONS}
            placeholder="All Plans"
          />
        </div>

        {error && (
          <p className="text-sm text-danger py-1">{error}</p>
        )}

        <DataTable
          columns={columns}
          rows={users}
          loading={loading}
          emptyText="No users found."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalCount={total}
          pageSize={PAGE_SIZE}
        />
      </div>

      {historyUser && <AnalysisHistoryModal user={historyUser} onClose={() => setHistoryUser(null)} />}
      {profileUser && <UserProfileModal     user={profileUser} onClose={() => setProfileUser(null)} />}
      {paymentUser && <PaymentHistoryModal  user={paymentUser} onClose={() => setPaymentUser(null)} />}
    </>
  );
};

export default AdminUserManagementPage;
